import React from 'react';
import VmkQuoteLineItem from './VmkQuoteLineItem';
import { formatVmkDate, isKeralitQuote, svgLooksLikeKeralit } from '../lib/vmkQuoteTable';
import { buildKeralitSavedSvg } from '../lib/keralitSvg';
import { formatQuoteDisplayReference } from '../lib/quoteNumber';
import { VmkBrandedPageHeader, VmkTemplateFooter } from './VmkBrandedTemplateChrome';

const VmkQuoteOfferBlock = ({ quoteNumber, clientName, showIntro = true, showDate = true }) => {
  const today = formatVmkDate();
  const displayReference = formatQuoteDisplayReference(quoteNumber, clientName);

  return (
    <div className="vmk-page-offer-block mb-3 leading-normal shrink-0">
      <div className="vmk-offerte-ref-box border border-black px-2 py-1 mb-2 grid grid-cols-2 gap-3">
        <span className="text-center"><strong>Offerte :</strong> {displayReference}</span>
        <span className="text-center"><strong>Referentie :</strong> {displayReference}</span>
      </div>

      {showDate && (
        <p className="vmk-header-date text-right mb-2">Enschede, {today}</p>
      )}

      {showIntro && (
        <div className="vmk-intro mb-3 space-y-1">
          <p>Geachte heer / mevrouw,</p>
          <p>Uw offerte t.b.v. bovengenoemd project bieden wij als volgt aan:</p>
        </div>
      )}
    </div>
  );
};

const VmkQuotePageHeader = ({ quoteNumber, clientName, showIntro }) => (
  <div className="vmk-page-header mb-3 leading-normal shrink-0">
    <div className="flex items-start justify-between gap-3 mb-2">
      <div>
        <div className="vmk-brand-title">VMK KOZIJNEN</div>
        <div className="vmk-brand-subtitle mt-0.5">KUNSTSTOF &amp; ALUMINIUM / KOZIJNEN / DEUREN / DAKKAPELLEN</div>
      </div>
      <div className="vmk-header-address text-right leading-snug">
        <p>Havenkade 64</p>
        <p>7553 GN Hengelo (ov)</p>
        <p>+31 (0)6 33 03 30 82</p>
        <p>Info@vmkkozijnen.nl</p>
      </div>
    </div>

    <VmkQuoteOfferBlock quoteNumber={quoteNumber} clientName={clientName} showIntro={showIntro} />
  </div>
);

const VmkQuoteSectionHeader = ({ sectionName, sectionIndex }) => {
  if (!sectionName) return null;
  return (
    <div className="vmk-section-header mb-3 shrink-0 border border-black bg-slate-50 px-3 py-2">
      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-800">
        {sectionIndex != null ? `${sectionIndex + 1}. ` : ''}{sectionName}
      </p>
    </div>
  );
};

const VmkQuoteTotals = ({
  totalPrice,
  formatPrice,
  subtotalExVatBeforeExportDeduction,
  exportDeductionExVat = 0,
  exportDeductionInclVat = 0,
  deductionLabel = 'Quote deduction',
  deductionLabelExVat = 'Quote deduction (excl. VAT)',
  sectionTitle = null,
}) => {
  const hasDeduction = exportDeductionExVat > 0;
  const subtotalBeforeDeduction = hasDeduction
    ? (subtotalExVatBeforeExportDeduction ?? totalPrice + exportDeductionExVat)
    : totalPrice;

  return (
    <div className="vmk-totals mt-3 shrink-0 w-full">
      {sectionTitle ? (
        <p className="text-[10px] font-semibold text-gray-800 mb-1.5 uppercase tracking-wide">
          {sectionTitle}
        </p>
      ) : null}
      <table className="text-[10px] w-full">
        <tbody>
          {hasDeduction && (
            <tr className="vmk-totals-row">
              <td className="vmk-totals-cell-label">
                <span className="vmk-totals-cell-inner vmk-totals-cell-inner--label">Subtotaal (exclusief BTW)</span>
              </td>
              <td className="vmk-totals-cell-value">
                <span className="vmk-totals-cell-inner vmk-totals-cell-inner--value">{formatPrice(subtotalBeforeDeduction)}</span>
              </td>
            </tr>
          )}
          {hasDeduction && (
            <tr className="vmk-totals-row vmk-deduction-row">
              <td className="vmk-totals-cell-label">
                <span className="vmk-totals-cell-inner vmk-totals-cell-inner--label">{deductionLabelExVat}</span>
              </td>
              <td className="vmk-totals-cell-value">
                <span className="vmk-totals-cell-inner vmk-totals-cell-inner--value vmk-deduction-amount">−{formatPrice(exportDeductionExVat)}</span>
              </td>
            </tr>
          )}
          <tr className="vmk-totals-row vmk-yellow-row">
          <td className="vmk-totals-cell-label">
            <span className="vmk-totals-cell-inner vmk-totals-cell-inner--label">Totaalprijs (exclusief BTW)</span>
          </td>
          <td className="vmk-totals-cell-value">
            <span className="vmk-totals-cell-inner vmk-totals-cell-inner--value font-medium">{formatPrice(totalPrice)}</span>
          </td>
        </tr>
        <tr className="vmk-totals-row vmk-yellow-row">
          <td className="vmk-totals-cell-label">
            <span className="vmk-totals-cell-inner vmk-totals-cell-inner--label">BTW 21,0% :</span>
          </td>
          <td className="vmk-totals-cell-value">
            <span className="vmk-totals-cell-inner vmk-totals-cell-inner--value">{formatPrice(totalPrice * 0.21)}</span>
          </td>
        </tr>
        <tr className="vmk-totals-row vmk-yellow-row">
          <td className="vmk-totals-cell-label">
            <span className="vmk-totals-cell-inner vmk-totals-cell-inner--label font-medium">Totaalprijs (inclusief BTW)</span>
          </td>
          <td className="vmk-totals-cell-value">
            <span className="vmk-totals-cell-inner vmk-totals-cell-inner--value font-medium">{formatPrice(totalPrice * 1.21)}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  );
};

const VmkQuoteLegalFooter = () => {
  const today = formatVmkDate();

  return (
    <div className="vmk-legal-footer mt-4 text-[10px] leading-normal space-y-1 shrink-0">
      <p>Huidige aanbieding blijft 2 maanden geldig.</p>
      <p>De gegeven afmetingen betreffen de buitenmaat in mm.</p>
      <p>Wanneer akkoord, graag alle bladen (van de offerte en tekeningen) getekend retour.</p>
      <p className="text-red-600 font-medium">Alle tekeningen zijn BUITENAANZICHT !</p>

      <div className="grid grid-cols-2 gap-6 mt-4 pt-3 border-t border-gray-300">
        <div>
          <p className="font-medium mb-4">Voor VMK Kozijnen BV,</p>
          <p>Naam : VMK Kozijnen</p>
          <p>Datum : {today}</p>
        </div>
        <div>
          <p className="font-medium mb-4">Voor akkoord bij bestelling,</p>
          <p>Naam :</p>
          <p>Datum :</p>
        </div>
      </div>
    </div>
  );
};

const VmkDrawingDivider = () => <div className="vmk-drawing-divider" aria-hidden="true" />;

const VmkQuotePageFooter = ({ pageNumber, quoteNumber, clientName }) => {
  const footerDate = formatVmkDate();
  const displayReference = formatQuoteDisplayReference(quoteNumber, clientName);
  const ref = displayReference ? `-${displayReference}-` : '-CONFI-GO-';

  return (
    <div className="vmk-page-footer text-[9px] text-gray-600 mt-auto pt-2 flex justify-between items-center shrink-0">
      <span>{ref}</span>
      <span>- {pageNumber} -</span>
      <span>-{footerDate}-</span>
    </div>
  );
};

const VmkQuotePricingPage = ({
  chunk,
  globalFirstIndex = 0,
  quoteNumber,
  clientName,
  showIntro,
  showTotals,
  showLegal,
  pageNumber,
  compact = false,
  tableSvgData,
  getSvgWithoutBackground,
  getQuoteLineTotal,
  formatPrice,
  t,
  isEditing,
  isExportingPDF,
  removeQuote,
  setTableSvgData,
  savedQuotes,
  totalPrice,
  subtotalExVatBeforeExportDeduction,
  exportDeductionExVat = 0,
  exportDeductionInclVat = 0,
  deductionLabel,
  deductionLabelExVat,
  showPricing = true,
  showNijBegun = false,
  useBrandedLayout = false,
  sectionName = null,
  sectionIndex = null,
  sectionTotalsTitle = null,
  showSectionHeader = false,
}) => {
  const lineItems = (
    <div className={`flex-1 min-h-0 overflow-hidden ${compact ? 'vmk-line-items-compact' : ''}`}>
      {chunk.map((quote, localIdx) => {
        const idx = globalFirstIndex + localIdx;
        const quoteIsKeralit = isKeralitQuote(quote, quote.type);
        // Do not paint another product's live SVG onto this drawing (e.g. Keralit logo on a door)
        const tableIsKeralit = svgLooksLikeKeralit(tableSvgData);
        const useTableSvg = Boolean(
          tableSvgData &&
            globalFirstIndex === 0 &&
            localIdx === 0 &&
            quoteIsKeralit === tableIsKeralit
        );
        const rawSvg = useTableSvg ? tableSvgData : (quote.svgData || '');
        const svgData = rawSvg
          ? getSvgWithoutBackground(
              quoteIsKeralit || svgLooksLikeKeralit(rawSvg)
                ? buildKeralitSavedSvg(quote.config)
                : rawSvg
            )
          : null;

        return (
          <React.Fragment key={quote.id || idx}>
            {localIdx > 0 ? <VmkDrawingDivider /> : null}
            <VmkQuoteLineItem
              quote={quote}
              index={idx}
              svgHtml={svgData}
              lineTotal={getQuoteLineTotal(quote)}
              formatPrice={formatPrice}
              drawingLoadingLabel={t('quote.drawingLoading')}
              compact={compact}
              showPricing={showPricing}
              showNijBegun={showNijBegun}
              showRemove={isEditing && !isExportingPDF && !!quote.id}
              removeLabel={t('savedQuotes.removeThisDrawing') || 'Remove this drawing'}
              onRemove={() => {
                if (window.confirm(t('savedQuotes.removeThisConfirm') || 'Remove this drawing from saved quotes?')) {
                  removeQuote(quote.id);
                  if (savedQuotes[0]?.id === quote.id) setTableSvgData(null);
                }
              }}
            />
          </React.Fragment>
        );
      })}
    </div>
  );

  const totalsProps = {
    totalPrice,
    formatPrice,
    subtotalExVatBeforeExportDeduction,
    exportDeductionExVat,
    exportDeductionInclVat,
    deductionLabel: deductionLabel || t('quote.exportDeductionLine'),
    deductionLabelExVat: deductionLabelExVat || t('quote.exportDeductionLineExVat'),
    sectionTitle: sectionTotalsTitle,
  };
  const sectionHeader = showSectionHeader ? (
    <VmkQuoteSectionHeader sectionName={sectionName} sectionIndex={sectionIndex} />
  ) : null;
  const deductionInclDisplay = exportDeductionInclVat > 0 ? exportDeductionInclVat : exportDeductionExVat;

  if (useBrandedLayout) {
    return (
      <div className="quote-pdf-page vmk-drawing-page mb-4" data-pdf-page data-pdf-drawing-page>
        <div className="vmk-proposal-shell">
          <VmkBrandedPageHeader pageNumber={pageNumber} />
          <div className="vmk-drawing-main">
            <VmkQuoteOfferBlock quoteNumber={quoteNumber} clientName={clientName} showDate={false} showIntro={false} />
            {sectionHeader}
            {lineItems}
            {showTotals && showPricing && exportDeductionExVat > 0 && (
              <p className="vmk-deduction-note text-[9px] text-slate-600 mt-2 leading-snug">
                {t('quote.exportDeductionNote', { amount: formatPrice(deductionInclDisplay) })}
              </p>
            )}
            {showTotals && showPricing && <VmkQuoteTotals {...totalsProps} />}
            {showLegal && <VmkQuoteLegalFooter />}
          </div>
          <VmkTemplateFooter className="vmk-proposal-footer" />
        </div>
      </div>
    );
  }

  return (
    <div className="quote-pdf-page mb-4" data-pdf-page data-pdf-drawing-page>
      <div className="quote-pdf-page-inner border-2 border-black">
        <VmkQuotePageHeader quoteNumber={quoteNumber} clientName={clientName} showIntro={showIntro} />
        {sectionHeader}
        {lineItems}
        {showTotals && showPricing && exportDeductionExVat > 0 && (
          <p className="vmk-deduction-note text-[9px] text-slate-600 mt-2 leading-snug">
            {t('quote.exportDeductionNote', { amount: formatPrice(deductionInclDisplay) })}
          </p>
        )}
        {showTotals && showPricing && <VmkQuoteTotals {...totalsProps} />}
        {showLegal && <VmkQuoteLegalFooter />}
        <VmkQuotePageFooter pageNumber={pageNumber} quoteNumber={quoteNumber} clientName={clientName} />
      </div>
    </div>
  );
};

export default VmkQuotePricingPage;
export { VmkQuoteTotals, VmkQuoteLegalFooter, VmkQuotePageFooter, VmkQuoteOfferBlock, VmkQuoteSectionHeader };
